/**
 * Dashboard Page - Stripe-style analytics dashboard
 *
 * Features:
 * - KPI cards with trends
 * - Task summary with status breakdown
 * - Pipeline overview
 * - Recent activity feed
 * - Quick actions
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { getDashboardData } from '../lib/services/insights.service';
import { getPriorityTasks } from '../lib/services/tasks.service';
import { getActiveSuggestions } from '../lib/services/assistant.service';
import { getTeamMemberById } from '../lib/services/team.service';
import { getBuildReviewStats } from '../lib/services/build-review.service';
import { getLandingPage } from '../lib/services/landing-page.service';
import type { EfficiencyMetric, ROIRecommendation } from '../lib/types';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserPlusIcon,
  DocumentPlusIcon,
  SparklesIcon,
  ChartBarIcon,
  BoltIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { format, formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';
import { TASK_STATUS_CONFIG, TASK_PRIORITY_CONFIG, type DashboardKPI, type ActivityItem, type ROIDiagnosis } from '../lib/types';

export function DashboardPage() {
  const { currentWorkspace, roiDiagnosis } = useWorkspace();

  if (!currentWorkspace) return null;

  const dashboardData = getDashboardData(currentWorkspace.id);
  const priorityTasks = getPriorityTasks(currentWorkspace.id).slice(0, 5);
  const suggestions = getActiveSuggestions(currentWorkspace.id).slice(0, 3);
  const buildStats = getBuildReviewStats();
  const landingPage = getLandingPage(currentWorkspace.id);

  return (
    <div className="p-0 sm:p-6 space-y-6 flex flex-col h-full bg-gray-50/50">
      {/* Top Banner - AI Assistant Quick Access (Floating Style) */}
      <div className="relative group overflow-hidden bg-white sm:rounded-2xl border-b sm:border border-gray-200/60 p-4 sm:p-5 shadow-sm hover:shadow-md transition-all duration-500">
        <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity">
           <SparklesIcon className="h-32 w-32 rotate-12" />
        </div>
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary-500 flex items-center justify-center shadow-lg shadow-primary-500/20 group-hover:scale-110 transition-transform duration-500">
              <SparklesIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">좋은 하루입니다, 지민 님!</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="flex -space-x-1">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-4 w-4 rounded-full border border-white bg-primary-100 flex items-center justify-center">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                    </div>
                  ))}
                </div>
                <p className="text-xs font-medium text-gray-500">
                   오늘 처리할 긴급 작업이 <span className="text-primary-600 font-bold">{dashboardData.taskSummary.overdue + priorityTasks.filter(t => t.priority === 'urgent').length}건</span> 있습니다.
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/assistant" className="btn-primary btn-sm rounded-xl px-4 py-2.5 shadow-lg shadow-primary-500/10 active:scale-95 transition-all">
              <BoltIcon className="h-4 w-4 mr-1.5" />
              AI와 대화하기
            </Link>
            <Link to="/build-review" className="btn-secondary btn-sm !bg-white rounded-xl border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all">
              <ShieldCheckIcon className="h-4 w-4 mr-1.5 text-emerald-500" />
              빌드 상태
            </Link>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-0 space-y-6 pb-12">
        {/* ROI Value Hero Section */}
        {roiDiagnosis && <ROIDiagnosisHero diagnosis={roiDiagnosis} />}

        {/* KPI Cards - Stripe-style */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {dashboardData.kpis.slice(0, 4).map((kpi) => (
            <KPICard key={kpi.id} kpi={kpi} />
          ))}
        </div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: AI Builder & Tasks */}
          <div className="lg:col-span-2 space-y-6">
            {/* Landing Page Preview - LIG-1321/LIG-1323 Enhanced */}
            <div className="card overflow-hidden group/card relative !p-0 border-none shadow-xl shadow-gray-200/50 bg-white">
              <div className="p-6 pb-2 flex items-center justify-between relative z-20">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                    <SparklesIcon className="h-5 w-5 text-indigo-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-bold text-gray-900 tracking-tight">AI 비주얼 빌더</h2>
                      <span className="px-1.5 py-0.5 rounded-md bg-emerald-50 text-[10px] font-black text-emerald-600 border border-emerald-100 uppercase tracking-tighter">Live Preview</span>
                    </div>
                    <p className="text-xs text-gray-500 font-medium">분석된 비즈니스 데이터를 기반으로 생성됨</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="flex -space-x-1">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-4 w-4 rounded-full border-2 border-white bg-indigo-100" />
                      ))}
                    </div>
                    <span className="text-[10px] font-bold text-gray-400">3명이 편집 중</span>
                  </div>
                  <div className="h-8 w-px bg-gray-100 mx-1" />
                  <Link to="/canvas" className="h-10 w-10 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200 flex items-center justify-center transition-colors group/edit">
                    <SparklesIcon className="h-5 w-5 text-gray-400 group-hover/edit:text-indigo-500 transition-colors" />
                  </Link>
                </div>
              </div>
              
              <div className="p-6 pt-4">
                <div className="relative aspect-[21/9] rounded-3xl border border-gray-100 bg-[#fbfbfc] overflow-hidden shadow-inner transition-all duration-700 group-hover/card:shadow-[0_30px_70px_-20px_rgba(0,0,0,0.15)] group-hover/card:border-indigo-100/50">
                  {/* Premium Mesh Gradient Background */}
                  <div className="absolute inset-0 opacity-[0.08] group-hover/card:opacity-[0.15] transition-opacity duration-700" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, ${landingPage.primaryColor} 1px, transparent 0)`, backgroundSize: '32px 32px' }} />
                  <div className="absolute top-[-20%] right-[-10%] w-[60%] h-[80%] rounded-full blur-[100px] opacity-20 animate-pulse-slow" style={{ backgroundColor: landingPage.primaryColor }} />
                  <div className="absolute bottom-[-20%] left-[-10%] w-[50%] h-[70%] rounded-full blur-[80px] opacity-15" style={{ backgroundColor: landingPage.accentColor }} />
                  
                  <div className="relative z-10 h-full flex flex-col items-center justify-center px-12 text-center py-10">
                    <div className="space-y-4 max-w-2xl transform transition-transform duration-700 group-hover/card:scale-[1.02]">
                      <div className="inline-block px-4 py-1.5 rounded-full bg-white/90 backdrop-blur-md border border-white/50 shadow-sm text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] mb-4 group-hover/card:translate-y-[-4px] transition-transform">
                        {currentWorkspace.businessType} • AI Generated Presence
                      </div>
                      <h3 className="text-4xl sm:text-6xl font-black text-gray-900 tracking-tighter leading-[1] mb-6">
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-950 via-gray-800 to-indigo-900">
                          {landingPage.title}
                        </span>
                      </h3>
                      <p className="text-gray-500 font-bold text-lg sm:text-xl leading-relaxed max-w-xl mx-auto opacity-80 group-hover/card:opacity-100 transition-opacity">
                        {landingPage.heroSubtitle}
                      </p>
                    </div>

                    <div className="mt-10 flex flex-wrap items-center justify-center gap-5">
                      <div className="group/btn relative">
                        <div className="absolute -inset-1 bg-gradient-to-r from-primary-400 to-indigo-400 blur-xl rounded-full opacity-0 group-hover/card:opacity-50 transition-opacity" />
                        <Link to="/canvas" className="relative h-14 px-10 rounded-2xl flex items-center justify-center gap-3 shadow-2xl group-hover/card:translate-y-[-6px] active:scale-95 transition-all duration-500 overflow-hidden" style={{ backgroundColor: landingPage.primaryColor }}>
                           <div className="absolute inset-0 bg-gradient-to-tr from-black/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity" />
                           <span className="text-white font-black text-sm tracking-widest uppercase">Launch Editor</span>
                           <ArrowTrendingUpIcon className="h-5 w-5 text-white rotate-45 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </Link>
                      </div>
                      <button className="h-14 px-8 rounded-2xl bg-white/80 backdrop-blur-md border border-white/50 text-gray-900 font-black text-sm shadow-xl hover:bg-white transition-all group-hover/card:translate-y-[-4px]">
                        Demo Mode
                      </button>
                    </div>
                  </div>
                  
                  {/* Floating SaaS Mockup Elements - Enhanced for LIG-1323 */}
                  <div className="absolute top-12 right-12 w-56 p-5 bg-white/90 backdrop-blur-3xl rounded-3xl shadow-[0_30px_60px_rgba(0,0,0,0.12)] border border-white/60 flex flex-col gap-4 animate-float group/item transition-all hover:scale-105 hover:rotate-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                          <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                        </div>
                        <div className="h-2.5 w-20 bg-gray-200 rounded-full" />
                      </div>
                      <div className="h-1.5 w-6 bg-gray-100 rounded-full" />
                    </div>
                    <div className="space-y-2">
                      <div className="w-full h-2 bg-gray-100 rounded-full" />
                      <div className="w-4/5 h-2 bg-gray-100 rounded-full" />
                      <div className="w-1/2 h-2 bg-indigo-100 rounded-full" />
                    </div>
                  </div>

                  <div className="absolute bottom-12 left-12 w-64 p-6 bg-white/90 backdrop-blur-3xl rounded-3xl shadow-[0_30px_60px_rgba(0,0,0,0.12)] border border-white/60 space-y-4 animate-float-delayed transition-all hover:scale-105 hover:-rotate-1">
                    <div className="flex justify-between items-center">
                       <div className="flex items-center gap-2">
                          <div className="h-2 w-16 bg-indigo-200 rounded-full" />
                          <div className="h-2 w-8 bg-indigo-100 rounded-full" />
                       </div>
                       <div className="h-6 w-6 rounded-lg bg-indigo-50 flex items-center justify-center">
                          <SparklesIcon className="h-3.5 w-3.5 text-indigo-400" />
                       </div>
                    </div>
                    <div className="h-4 w-full bg-indigo-50 rounded-xl overflow-hidden relative shadow-inner">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-400 to-primary-400 w-3/4 rounded-xl shadow-sm" />
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="h-1.5 w-10 bg-gray-100 rounded-full" />
                      <div className="text-[10px] font-black text-indigo-500 uppercase">75% Complete</div>
                    </div>
                  </div>

                  {/* Gradient Overlay for depth */}
                  <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#fbfbfc] via-[#fbfbfc]/60 to-transparent pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Task Status Overview */}
            <div className="card">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-1 bg-primary-500 rounded-full" />
                  <h2 className="text-lg font-bold text-gray-900">작업 파이프라인</h2>
                </div>
                <Link to="/tasks" className="text-sm text-primary-600 hover:text-primary-700 font-bold flex items-center gap-1 group">
                  전체 보기 
                  <MinusIcon className="h-3 w-3 rotate-90 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
              <div className="grid grid-cols-5 gap-4">
                {(['triage', 'backlog', 'in_progress', 'in_review', 'done'] as const).map((status) => {
                  const config = TASK_STATUS_CONFIG[status];
                  const count = dashboardData.taskSummary.byStatus[status];
                  return (
                    <div key={status} className="group relative text-center p-4 rounded-2xl bg-gray-50/50 hover:bg-white border border-transparent hover:border-gray-100 hover:shadow-sm transition-all duration-300">
                      <div className="flex items-center justify-center gap-1.5 mb-2">
                        <span className={`h-2 w-2 rounded-full status-dot-${config.color}`} />
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{config.label}</span>
                      </div>
                      <span className="text-3xl font-black text-gray-900 group-hover:scale-110 transition-transform block">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Priority Tasks */}
            <div className="card">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-1 bg-red-500 rounded-full" />
                  <h2 className="text-lg font-bold text-gray-900">우선순위 작업</h2>
                </div>
                {dashboardData.taskSummary.overdue > 0 && (
                  <span className="badge !bg-red-50 !text-red-600 !border-red-100 px-3 py-1 font-bold animate-bounce">
                    <ExclamationTriangleIcon className="h-3 w-3 mr-1.5" />
                    {dashboardData.taskSummary.overdue} overdue
                  </span>
                )}
              </div>
              <div className="space-y-3">
                {priorityTasks.length === 0 ? (
                  <div className="empty-state py-12 bg-gray-50/30 border-dashed border-2 border-gray-100 rounded-3xl">
                    <div className="h-16 w-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-4">
                      <CheckCircleIcon className="h-8 w-8 text-emerald-500" />
                    </div>
                    <p className="empty-state-title text-gray-900 font-bold">완벽한 하루네요!</p>
                    <p className="empty-state-description text-gray-500 font-medium">현재 긴급하게 처리할 작업이 없습니다.</p>
                  </div>
                ) : (
                  priorityTasks.map((task) => {
                    const statusConfig = TASK_STATUS_CONFIG[task.status];
                    const priorityConfig = TASK_PRIORITY_CONFIG[task.priority];
                    const assignee = task.assigneeId ? getTeamMemberById(task.assigneeId) : null;

                    return (
                      <div
                        key={task.id}
                        className="group flex items-center justify-between p-4 rounded-2xl border border-gray-100 hover:border-primary-200 hover:bg-primary-50/30 hover:shadow-md hover:shadow-primary-500/5 transition-all duration-300"
                      >
                        <div className="flex items-center gap-4 min-w-0">
                          <div className={clsx(
                            'h-10 w-10 rounded-xl flex items-center justify-center shrink-0 transition-colors',
                            `bg-${statusConfig.color}-50 text-${statusConfig.color}-500 group-hover:bg-white`
                          )}>
                             <div className={`h-3 w-3 rounded-full bg-${statusConfig.color}-500 shadow-[0_0_8px_rgba(var(--tw-color-${statusConfig.color}-500),0.5)]`} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-gray-400 font-black tracking-widest">{task.identifier}</span>
                              <span className="font-bold text-gray-900 truncate group-hover:text-primary-900 transition-colors">{task.title}</span>
                            </div>
                            {task.dueDate && (
                              <div className="flex items-center gap-1.5 mt-1">
                                <ClockIcon className="h-3 w-3 text-gray-400" />
                                <span className={clsx(
                                  'text-[11px] font-bold',
                                  new Date(task.dueDate) < new Date() ? 'text-red-500' : 'text-gray-400'
                                )}>
                                  {format(new Date(task.dueDate), 'MMM d')} 마감
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={clsx(
                            'text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border',
                            priorityConfig.color === 'red' ? 'bg-red-50 text-red-600 border-red-100' : 
                            priorityConfig.color === 'orange' ? 'bg-orange-50 text-orange-600 border-orange-100' : 
                            priorityConfig.color === 'blue' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-gray-50 text-gray-500 border-gray-100'
                          )}>
                            {priorityConfig.label}
                          </span>
                          {assignee && (
                            <div className="h-8 w-8 rounded-full bg-gray-100 border-2 border-white shadow-sm flex items-center justify-center text-[10px] font-black text-gray-500 overflow-hidden ring-1 ring-gray-100" title={assignee.name}>
                              {assignee.avatar ? <img src={assignee.avatar} className="h-full w-full object-cover" /> : assignee.name.charAt(0)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Build Review & Assistant */}
          <div className="space-y-6">
            {/* Build Health Summary - Compact & Actionable */}
            <Link to="/build-review" className="card !p-0 overflow-hidden group hover:shadow-xl hover:shadow-emerald-500/5 transition-all duration-500 block border-none shadow-lg shadow-gray-200/50">
              <div className="p-5 flex items-center justify-between border-b border-gray-50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ShieldCheckIcon className="h-5 w-5 text-emerald-500" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 tracking-tight">빌드 상태</h2>
                </div>
                <div className={clsx(
                  'px-2.5 py-1 rounded-lg flex items-center gap-2 transition-all',
                  buildStats.latestRunStatus === 'passed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                )}>
                  <div className={clsx(
                    'h-1.5 w-1.5 rounded-full relative',
                    buildStats.latestRunStatus === 'passed' ? 'bg-emerald-500' : 'bg-amber-500'
                  )}>
                    <div className="absolute inset-0 rounded-full animate-ping opacity-75 bg-current" />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest">
                    {buildStats.latestRunStatus === 'passed' ? 'Operational' : 'Building'}
                  </span>
                </div>
              </div>
              <div className="p-5 space-y-4 bg-white">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Open Blockers</p>
                    <p className={clsx('text-2xl font-black', buildStats.openBlockers > 0 ? 'text-red-500' : 'text-gray-900')}>
                      {buildStats.openBlockers}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Sync Coverage</p>
                    <p className="text-2xl font-black text-gray-900">{buildStats.syncCoverage}%</p>
                  </div>
                </div>
                <div className="w-full h-2 bg-gray-50 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all duration-1000" style={{ width: `${buildStats.syncCoverage}%` }} />
                </div>
              </div>
            </Link>

            {/* AI Assistant Quick Card */}
            <div className="card !p-0 overflow-hidden shadow-lg shadow-gray-200/50 border-none group">
              <div className="p-5 bg-gradient-to-br from-primary-600 to-primary-700 text-white">
                <div className="flex items-center justify-between mb-4">
                  <SparklesIcon className="h-8 w-8 opacity-40" />
                  <span className="text-[10px] font-black uppercase tracking-widest bg-white/20 px-2 py-1 rounded-lg backdrop-blur-md">Smart Insights</span>
                </div>
                <h3 className="text-lg font-bold leading-tight">지민 님의 비즈니스를 위한 AI 추천</h3>
              </div>
              <div className="p-3 space-y-2 bg-white">
                {suggestions.length === 0 ? (
                  <p className="text-sm text-gray-500 p-4 text-center">현재 새로운 제안이 없습니다.</p>
                ) : (
                  suggestions.map((suggestion) => (
                    <Link key={suggestion.id} to="/assistant" className="block p-3 rounded-xl bg-gray-50 hover:bg-primary-50 border border-gray-100 hover:border-primary-100 transition-all group/sug">
                      <p className="text-sm font-bold text-gray-900 group-hover/sug:text-primary-700 transition-colors">{suggestion.title}</p>
                      <p className="text-[11px] text-gray-500 mt-1 line-clamp-2 leading-relaxed">{suggestion.description}</p>
                    </Link>
                  ))
                )}
                <Link to="/assistant" className="block py-3 text-center text-xs font-bold text-primary-600 hover:text-primary-700 transition-colors pt-4">
                  전체 제안 보기 →
                </Link>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="card">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-1 bg-gray-200 rounded-full" />
                  <h2 className="text-lg font-bold text-gray-900">최근 활동</h2>
                </div>
              </div>
              <div className="space-y-1">
                {dashboardData.recentActivity.slice(0, 5).map((activity) => (
                  <ActivityItemComponent key={activity.id} activity={activity} />
                ))}
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-1 gap-4">
              <div className="card bg-gray-900 text-white border-none shadow-xl shadow-gray-900/10">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Active Members</p>
                  <div className="h-6 w-6 rounded-lg bg-white/10 flex items-center justify-center">
                    <UserPlusIcon className="h-3.5 w-3.5 text-white" />
                  </div>
                </div>
                <p className="text-3xl font-black">{dashboardData.teamSummary.active}</p>
                <div className="mt-4 flex -space-x-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="h-8 w-8 rounded-full border-2 border-gray-900 bg-gray-800 flex items-center justify-center text-[10px] font-bold text-gray-400">
                      M{i}
                    </div>
                  ))}
                  <div className="h-8 w-8 rounded-full border-2 border-gray-900 bg-primary-500 flex items-center justify-center text-[10px] font-bold text-white">
                    +{dashboardData.teamSummary.active - 4}
                  </div>
                </div>
              </div>

              <div className="card !bg-white">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Pipeline Value</p>
                  <div className="h-6 w-6 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <CurrencyDollarIcon className="h-3.5 w-3.5 text-emerald-500" />
                  </div>
                </div>
                <p className="text-3xl font-black text-gray-900">₩{dashboardData.crmSummary.totalDealValue.toLocaleString()}</p>
                <div className="mt-2 flex items-center gap-2">
                   <span className="text-[11px] font-bold text-emerald-500">+12.5%</span>
                   <span className="text-[11px] font-medium text-gray-400">vs last month</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ROIDiagnosisHero({ diagnosis }: { diagnosis: ROIDiagnosis }) {
  return (
    <section className="rounded-2xl bg-gradient-to-r from-slate-900 via-primary-900 to-accent-800 p-6 text-white shadow-sm">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary-100">
            <BoltIcon className="h-4 w-4" />
            ROI / Efficiency Diagnosis
          </div>
          <h2 className="mt-4 text-2xl font-semibold">이번 달 바로 체감할 수 있는 운영 개선 효과</h2>
          <p className="mt-2 text-sm text-primary-50/90">
            현재 운영 점수 {diagnosis.overallScore}점을 기준으로, Vice가 반복 업무를 정리하고 고객/작업 흐름을 묶으면
            월 {diagnosis.estimatedTimeSavedHours}시간과 ₩{diagnosis.estimatedMonthlySavings.toLocaleString()} 수준의 절감 여지를 만들 수 있습니다.
          </p>
        </div>

        <div className="grid min-w-full grid-cols-2 gap-3 lg:min-w-[320px] lg:max-w-sm">
          <div className="rounded-2xl bg-white/10 p-4 backdrop-blur-sm">
            <div className="flex items-center gap-2 text-primary-100">
              <CurrencyDollarIcon className="h-4 w-4" />
              <span className="text-xs font-medium">Monthly Savings</span>
            </div>
            <div className="mt-2 text-2xl font-bold">₩{diagnosis.estimatedMonthlySavings.toLocaleString()}</div>
          </div>
          <div className="rounded-2xl bg-white/10 p-4 backdrop-blur-sm">
            <div className="flex items-center gap-2 text-primary-100">
              <ClockIcon className="h-4 w-4" />
              <span className="text-xs font-medium">Time Saved</span>
            </div>
            <div className="mt-2 text-2xl font-bold">{diagnosis.estimatedTimeSavedHours}h</div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-3">
        <MetricCard metric={diagnosis.timeEfficiency} icon={<ClockIcon className="h-5 w-5" />} />
        <MetricCard metric={diagnosis.costEfficiency} icon={<CurrencyDollarIcon className="h-5 w-5" />} />
        <MetricCard metric={diagnosis.automationPotential} icon={<ChartBarIcon className="h-5 w-5" />} />
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-3">
        {diagnosis.recommendations.slice(0, 3).map((recommendation) => (
          <RecommendationCard key={recommendation.id} recommendation={recommendation} />
        ))}
      </div>
    </section>
  );
}

function MetricCard({ metric, icon }: { metric: EfficiencyMetric; icon: ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-primary-50">
        {icon}
        {metric.label}
      </div>
      <div className="mt-3 flex items-end gap-2">
        <span className="text-3xl font-bold">{metric.potentialScore}</span>
        <span className="pb-1 text-sm text-emerald-300">+{metric.improvement}%</span>
      </div>
      <p className="mt-2 text-xs leading-5 text-primary-100/80">{metric.description}</p>
    </div>
  );
}

function RecommendationCard({ recommendation }: { recommendation: ROIRecommendation }) {
  const impactLabel = recommendation.impact === 'high' ? 'High impact' : recommendation.impact === 'medium' ? 'Medium impact' : 'Quick win';

  return (
    <div className="rounded-2xl border border-white/10 bg-slate-950/20 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-white">{recommendation.title}</p>
        <span className="rounded-full bg-white/10 px-2 py-1 text-[11px] text-primary-50">{impactLabel}</span>
      </div>
      <p className="mt-2 text-xs leading-5 text-primary-100/80">{recommendation.description}</p>
      <div className="mt-3 text-xs font-medium text-emerald-300">
        {recommendation.estimatedSavings ? `₩${recommendation.estimatedSavings.toLocaleString()} 절감` : ''}
        {recommendation.estimatedSavings && recommendation.estimatedTimeHours ? ' · ' : ''}
        {recommendation.estimatedTimeHours ? `${recommendation.estimatedTimeHours}시간/월 절약` : ''}
      </div>
    </div>
  );
}

function KPICard({ kpi }: { kpi: DashboardKPI }) {
  const formatValue = (value: number, format: DashboardKPI['format']) => {
    switch (format) {
      case 'currency':
        return `₩${value.toLocaleString()}`;
      case 'percentage':
        return `${value}%`;
      default:
        return value.toLocaleString();
    }
  };

  const TrendIcon = kpi.trend === 'up' ? ArrowTrendingUpIcon : kpi.trend === 'down' ? ArrowTrendingDownIcon : MinusIcon;

  return (
    <div className="card-compact">
      <p className="kpi-card-label">{kpi.label}</p>
      <p className="kpi-card-value text-2xl">{formatValue(kpi.value, kpi.format)}</p>
      {kpi.change !== undefined && (
        <div className={clsx('kpi-card-change', kpi.trend === 'up' ? 'kpi-card-change-up' : kpi.trend === 'down' ? 'kpi-card-change-down' : 'text-gray-500')}>
          <TrendIcon className="h-3 w-3" />
          <span>{Math.abs(kpi.change)}%</span>
        </div>
      )}
    </div>
  );
}

function ActivityItemComponent({ activity }: { activity: ActivityItem }) {
  const getActivityIcon = () => {
    switch (activity.type) {
      case 'task_completed':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'task_created':
        return <DocumentPlusIcon className="h-4 w-4 text-blue-500" />;
      case 'contact_added':
        return <UserPlusIcon className="h-4 w-4 text-purple-500" />;
      case 'deal_won':
        return <SparklesIcon className="h-4 w-4 text-amber-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  return (
    <div className="activity-item py-2">
      <div className="activity-icon bg-gray-100 rounded-full p-1.5">
        {getActivityIcon()}
      </div>
      <div className="activity-content">
        <p className="activity-title text-xs">{activity.title}</p>
        <p className="activity-time">
          {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
        </p>
      </div>
    </div>
  );
}

export default DashboardPage;
